import React, { useCallback, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import _ from 'lodash';
import { Spinner } from '@vibrent/electryon';
import { useTranslation } from 'react-i18next';
import styles from './styles.scss';
import { CHILD_MESSAGE_TYPES, IFRAME_SANDBOX } from './constants';
import {
  buildContextMessage,
  buildTokenRefreshMessage,
  getModuleOrigin,
  isInternalPath,
} from './utils';

const CLASS_NAME = styles['external-module-frame'];

/**
 * Hosts an external module in an iframe and talks to it over postMessage.
 *  - parent -> child: the context on load, whenever the child asks for it and whenever it
 *    changes, and the refreshed token whenever `token` changes.
 *  - child -> parent: `ready` (re-send the context) and `navigate` (open an RC page).
 * Every message is pinned to the module's origin in both directions.
 */
const ModuleFrame = ({ url, title, context, token, onNavigate }) => {
  const { t } = useTranslation();
  const iframeRef = useRef(null);
  const contextRef = useRef(context);
  const lastTokenRef = useRef(token);
  const [loading, setLoading] = useState(true);
  const origin = getModuleOrigin(url);
  contextRef.current = context;
  // The token is relayed on its own below, so only a change to the rest of the context
  // (e.g. another participant being opened) triggers a new context message.
  const contextKey = JSON.stringify(_.omit(context, 'token'));
  const lastContextKeyRef = useRef(contextKey);

  const postToChild = useCallback((message) => {
    const childWindow = iframeRef.current && iframeRef.current.contentWindow;
    if (childWindow && origin) {
      childWindow.postMessage(message, origin);
    }
  }, [origin]);

  const sendContext = useCallback(() => {
    postToChild(buildContextMessage(contextRef.current));
  }, [postToChild]);

  useEffect(() => {
    const onMessage = (event) => {
      const fromChild = iframeRef.current && event.source === iframeRef.current.contentWindow;
      const data = event.data;
      if (event.origin !== origin || !fromChild || !data || typeof data !== 'object') {
        return;
      }
      if (data.type === CHILD_MESSAGE_TYPES.READY) {
        sendContext();
      } else if (data.type === CHILD_MESSAGE_TYPES.NAVIGATE && isInternalPath(data.path)) {
        onNavigate(data.path);
      }
    };
    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, [origin, sendContext, onNavigate]);

  useEffect(() => {
    if (lastContextKeyRef.current !== contextKey) {
      lastContextKeyRef.current = contextKey;
      sendContext();
    }
  }, [contextKey, sendContext]);

  useEffect(() => {
    if (lastTokenRef.current !== token) {
      lastTokenRef.current = token;
      postToChild(buildTokenRefreshMessage(token));
    }
  }, [token, postToChild]);

  if (!origin) {
    return <div className={CLASS_NAME}>{t('This module cannot be loaded because its URL is not allowed.')}</div>;
  }
  return (
    <div className={CLASS_NAME}>
      {loading && <div className="module-loading"><Spinner radius={60} /></div>}
      <iframe
        ref={iframeRef}
        title={title}
        src={url}
        sandbox={IFRAME_SANDBOX}
        onLoad={() => {
          setLoading(false);
          sendContext();
        }}
      />
    </div>
  );
};

ModuleFrame.propTypes = {
  url: PropTypes.string.isRequired,
  title: PropTypes.string.isRequired,
  context: PropTypes.shape({}).isRequired,
  token: PropTypes.string,
  onNavigate: PropTypes.func.isRequired,
};

ModuleFrame.defaultProps = {
  token: '',
};

export default ModuleFrame;
